import React from 'react';
import { useTranslation } from '@refinedev/core';
import { ListButton, RefreshButton, Show } from '@refinedev/antd';
import { Typography, Card, Space, Tag } from 'antd';
import {
  ContactsOutlined,
  MailOutlined,
  TeamOutlined,
  MessageOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { endPoints } from '@/api/configs';
import { useGet } from '@/api/useApi';
import { ContactRes } from '@/api/types/contactUs';

const { Title, Text } = Typography;

const InfoCard = ({
  icon: Icon,
  label,
  children,
}: {
  icon: any;
  label: string;
  children: React.ReactNode;
}) => (
  <div className=" p-4 rounded-lg border border-gray-600">
    <div className="flex items-center gap-2 text-gray-600 mb-2">
      <Icon />
      <span className="font-medium">{label}</span>
    </div>
    <div className="pl-6">{children}</div>
  </div>
);

export const ContactUsShow = () => {
  const { id } = useParams();
  const { translate } = useTranslation();
  const { data, isLoading } = useGet<ContactRes>(
    endPoints.contactUs(id),
    endPoints.contactUs(id)
  );
  const record = data?.data.data;

  return (
    <Show
      isLoading={isLoading}
      title={
        <div className="flex items-center gap-2">
          <ContactsOutlined className="text-2xl" />
          <Title level={4} className="!m-0">
            {translate('contact-us.show.title')}
          </Title>
        </div>
      }
      headerButtons={({ listButtonProps }) => (
        <>
          {listButtonProps && (
            <Space>
              <ListButton {...listButtonProps}>
                {translate('buttons.list')}
              </ListButton>
              <RefreshButton />
            </Space>
          )}
        </>
      )}
    >
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCard
              icon={MailOutlined}
              label={translate('listing-contact-us.email')}
            >
              <Text copyable className="break-all">
                {record?.email}
              </Text>
            </InfoCard>

            <InfoCard
              icon={TeamOutlined}
              label={translate('listing-contact-us.organization_name')}
            >
              <Text strong className="break-words">
                {record?.organization_name}
              </Text>
            </InfoCard>
          </div>

          <InfoCard
            icon={MessageOutlined}
            label={translate('listing-contact-us.message')}
          >
            <div className="break-words whitespace-pre-wrap">
              {record?.message}
            </div>
          </InfoCard>

          <InfoCard
            icon={ClockCircleOutlined}
            label={translate('listing-contact-us.created_at')}
          >
            {record?.created_at && (
              <Tag color="blue" className="text-sm">
                {dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Tag>
            )}
          </InfoCard>
        </div>
      </Card>
    </Show>
  );
};
